import React from 'react';
import { useSelector } from 'react-redux';
import { isSlidingDoorsInfoSheetVisible, hideSlidingDoorsInfoSheet } from 'ducks/sheets';
import SheetBox from 'ui/Sheet/SheetBox';
import SheetHeader from 'ui/Sheet/SheetHeader';
import Sheet from 'ui/Sheet/Sheet';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import styled from 'styled-components';
import FrameOrder50_50_50_50 from 'components/ProductSidebar/CategoryWidget/icons/50_50_50_50.svg?react';
import FrameOrder100_50_50 from 'components/ProductSidebar/CategoryWidget/icons/100_50_50.svg?react';
import FrameOrder50_50_100 from 'components/ProductSidebar/CategoryWidget/icons/50_50_100.svg?react';
import FrameOrder100_100 from 'components/ProductSidebar/CategoryWidget/icons/100_100.svg?react';
import FrameOrder75_75 from 'components/ProductSidebar/CategoryWidget/icons/75_75.svg?react';
import { useIntl } from 'react-intl';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import Title from 'ui/Typography/Title';
import { KompisSheetFooter, KompisSheetFooterButtons } from '@inter-ikea-kompis/react-components';
import { useAppDispatch } from 'ducks/hooks';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useTranslations } from 'hooks/useTranslations';
import { useServiceSettings } from 'contexts/serviceSettings';

const Alternatives = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-evenly;
  padding-top: ${({ theme }) => theme.space.xl};
`;

const FrameOrder = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${({ theme }) => theme.space.md} 0;
  padding: ${({ theme }) => theme.space.xxs};
`;

const alternatives = [
  {
    label: '50+50+100',
    icon: FrameOrder50_50_100
  },
  {
    label: '100+50+50',
    icon: FrameOrder100_50_50
  },
  {
    label: '100+100',
    icon: FrameOrder100_100
  },
  {
    label: '50+50+50+50',
    icon: FrameOrder50_50_50_50
  },
  {
    label: '75+75',
    icon: FrameOrder75_75
  }
];

function SlidingDoorsInfoSheet() {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { translations } = useServiceSettings();
  const { useMetric } = useTranslations();

  const convertLabelMeasurementsToLocalizedMeasurements = (label: string) => {
    const labelArr = label.split('+').map(Number);

    const localizedLabelArr = labelArr.map((label: number) => {
      return adjustMeasurementsSystem(label * 10, translations, useMetric);
    });

    return localizedLabelArr.join(' + ');
  };

  const visible = useSelector(isSlidingDoorsInfoSheetVisible);

  const onClose = () => {
    dispatch(hideSlidingDoorsInfoSheet());
  };

  return (
    <Sheet onOverlayClick={onClose} visible={visible} data-testid="sliding-doors-info-sheet">
      <SheetHeader onCloseButtonClick={onClose} />
      <SheetBox style={{ height: '100%' }}>
        <Title type={ThemeFontStyleTypeEnum.headingMedium}>
          {formatMessage({ id: 'storageone.slidingDoorsInfoSheetTitle' })}
        </Title>
        <Body pt="md" type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.slidingDoorFrameOrderContent' })}
        </Body>
        <Alternatives>
          {alternatives.map(({ icon: Icon, label }) => {
            const localizedLabel = convertLabelMeasurementsToLocalizedMeasurements(label);

            return (
              <FrameOrder key={localizedLabel}>
                <Icon />
                <Body type={ThemeFontStyleTypeEnum.bodySmall} textAlign="center">
                  {localizedLabel}
                </Body>
              </FrameOrder>
            );
          })}
        </Alternatives>
      </SheetBox>

      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          theme={SkapaTheme}
          primaryLabel={formatMessage({ id: 'storageone.mobileFirstTimeInfoToastButtonText' })}
          onButtonClick={onClose}
        />
      </KompisSheetFooter>
    </Sheet>
  );
}

export default SlidingDoorsInfoSheet;
